import React, { useEffect, useState, useRef } from 'react';
import { ClipboardPaste, X, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import './pastedrawer.css';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { t } from '../../locales/translate';

const PastePlanDrawer = ({ isOpen, onClose, onPasteClick, refreshKey, pasteMonth, pasteYear }) => {
  const [copiedPlans, setCopiedPlans] = useState([]);
  const [openIndexes, setOpenIndexes] = useState({});
  const [targetDates, setTargetDates] = useState({});
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const drawerRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target) && !isDatePickerOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isDatePickerOpen]);

  useEffect(() => {
    if (isOpen) {
      const raw = localStorage.getItem('copiedPlanData');
      const parsed = raw ? JSON.parse(raw) : [];

      const defaultDates = {};
      parsed.forEach((plan, index) => {
        const original = new Date(plan.date);
        const defaultTarget = new Date(pasteYear, pasteMonth - 1, original.getDate());
        defaultDates[index] = defaultTarget;
      });

      setCopiedPlans(Array.isArray(parsed) ? parsed : []);
      setTargetDates(defaultDates);
    }
  }, [isOpen, refreshKey, pasteMonth, pasteYear]);

  const toggleDropdown = (index) => {
    setOpenIndexes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (time) => {
    return new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDateChange = (index, newDate) => {
    setTargetDates((prev) => ({
      ...prev,
      [index]: newDate,
    }));
  };

  useEffect(() => {
    if (Object.keys(targetDates).length > 0) {
      const isoDates = {};

      Object.entries(targetDates).forEach(([key, date]) => {
        if (date instanceof Date && !isNaN(date)) {
          isoDates[key] = date.toISOString();
        } else {
          console.warn("Invalid date at key", key, date);
        }
      });

      localStorage.setItem("targetPlanDates", JSON.stringify(isoDates));
    }
  }, [targetDates]);

  return (
    <>
    <div className='paste-drawer-overlay'>
      <div ref={drawerRef} className={`paste-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <div className="drawer-header-left">
            <div className="paste-drawer-header-icon">
              <ClipboardPaste size={24} color="white" />
            </div>
            <div className="drawer-header-texts">
              <h3 className="drawer-title">{t("pastePlan")}</h3>
              <p className="drawer-subtext">
                {t("pastePlanTip")}
              </p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="drawer-content">
          {copiedPlans.length === 0 ? (
            <p>No copied plans found.</p>
          ) : (
            <div className="copied-plan-list">
              {copiedPlans.map((plan, index) => (
                <div key={index} className="copied-plan-entry">
                  <div className="copied-plan-header">
                    <div className='selected-dates-dropdown' onClick={() => toggleDropdown(index)}>
                      {formatDate(plan.date)}
                      <span style={{ marginLeft: '6px' }}>
                        {openIndexes[index] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </div>

                    <div style={{ width: '80px' }}>
                      <ArrowRight size={16} />
                    </div>

                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={targetDates[index] || null}
                          onChange={(newDate) => handleDateChange(index, newDate)}
                          format="MMMM d, yyyy"
                          onOpen={() => setIsDatePickerOpen(true)}
                          onClose={() => setIsDatePickerOpen(false)}
                          slots={{ textField: TextField }}
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              placeholder: "Select date",
                              InputProps: {
                                disableUnderline: true,
                              },
                              sx: {
                                width: "100%",
                                '& .MuiOutlinedInput-root': {
                                  height: "40px",
                                  borderRadius: "60px",
                                  backgroundColor: "white",
                                  border: '1px solid #ccc',
                                  '& fieldset': {
                                    border: 'none',
                                  },
                                  '&:hover fieldset': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none',
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                },
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>

                  {openIndexes[index] && (
                    <div
                      className="copied-plan-details"
                      style={{ paddingLeft: '12px', marginTop: '6px' }}
                    >
                      {plan.planDetails?.length > 0 ? (
                        plan.planDetails.map((detail, i) => {
                          const time = `${formatTime(detail.startTime)} - ${formatTime(
                            detail.endTime
                          )}`;
                          const location = detail.practiceLocationName || 'N/A';
                          const doctor = detail.doctorName || 'N/A';

                          return (
                            <div key={i} style={{ marginBottom: '4px', fontSize: '14px' }}>
                              {time} {location} {doctor}
                            </div>
                          );
                        })
                      ) : (
                        <div>No visit details.</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <hr />
        <div className="paste-drawer-footer">
          <button className="paste-drawer-cancel-button" onClick={onClose} style={{width:  document.body.dir === "rtl" ? "119px" : ""}}>
           {t("cancel")}
          </button>
          <button className="paste-drawer-paste-button" onClick={onPasteClick} style={{width:  document.body.dir === "rtl" ? "123px" : ""}}>{t("paste")}</button>
        </div>
      </div>
      </div>
    </>
  );
};

export default PastePlanDrawer;